import getPageParentBySlug from 'app/utilities/get-page-parent-by-slug.js';
import JsonLd from 'app/utilities/JsonLd.jsx';
import React from 'react';
import { seoDomain } from 'config/seo';

// The homepage holds position 1, so anything returned by the tail starts after it
const TAIL_START = 2;

/**
 * Outputs breadcrumb list items following the homepage.
 *
 * @param  {object} pageData  Page data object with properties such as attributes, types, etc.
 *
 * @return {array}            Breadcrumb list items.
 */
const breadcrumbListTail = (pageData) => {
    const { attributes, type } = pageData;

    const { slug, name } = attributes;

    // The homepage has only one breadcrumb list item.
    if (slug === 'home') {
        return [];
    }

    if (type === 'animals') {
        return [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `${seoDomain}/animals`,
                    name: 'Animals'
                }
            },
            {
                '@type': 'ListItem',
                position: 3,
                item: {
                    '@id': `${seoDomain}/animals/${slug}`,
                    name
                }
            }
        ];
    }

    if (type === 'news') {
        return [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `${seoDomain}/news`,
                    name: 'News'
                }
            },
            {
                '@type': 'ListItem',
                position: 3,
                item: {
                    '@id': `${seoDomain}/news/${slug}`,
                    name
                }
            }
        ];
    }

    if (type === 'news-category' || type === 'news-categories') {
        const { parent: parentSlug, parent_name: parentName } = attributes;

        // A level 2 category sits under its parent, so the trail is either two or
        // three deep. Built as a plain list and numbered from its own order, so
        // the positions stay a run of consecutive ordinals either way.
        const trail = [{ id: `${seoDomain}/news`, name: 'News' }];

        if (parentSlug && parentName) {
            trail.push({ id: `${seoDomain}/news/${parentSlug}`, name: parentName });
        }

        trail.push({
            id: parentSlug ? `${seoDomain}/news/${parentSlug}/${slug}` : `${seoDomain}/news/${slug}`,
            name: name
        });

        return trail.map(({ id, name: itemName }, index) => ({
            '@type': 'ListItem',
            position: index + TAIL_START,
            item: {
                '@id': id,
                name: itemName
            }
        }));
    }

    // Pages with parents have three breadcrumb list items.
    if (attributes.parent) {
        const { slug: parentSlug, name: parentName } = getPageParentBySlug(attributes.parent);

        return [
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    '@id': `${seoDomain}/${parentSlug}`,
                    name: parentName
                }
            },
            {
                '@type': 'ListItem',
                position: 3,
                item: {
                    '@id': `${seoDomain}/${parentSlug}/${slug}`,
                    name
                }
            }
        ];
    }

    return [];
};

/**
 * Generates a breadcrumb list structured data object.
 *
 * @param  {object} pageData  Page data object with properties such as attributes, type, etc.
 *
 * @return {object|null}      Structured data object, or null when no trail exists.
 */
export const breadcrumbList = (pageData) => {
    const tail = breadcrumbListTail(pageData);

    if (tail.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                item: {
                    '@id': seoDomain,
                    name: 'Auckland Zoo'
                }
            },
            ...tail
        ]
    };
};

/**
 * Breadcrumb markup guide can be found here:
 * https://developers.google.com/search/docs/data-types/breadcrumb
 *
 * @param  {object} pageData  Page data object with properties such as attributes, type, etc.
 *
 * @return {object|null}      JSX with the structured data, or null when no trail exists
 */
export default (pageData) => { // eslint-disable-line react/display-name
    const item = breadcrumbList(pageData);

    if (!item) {
        return null;
    }

    return <JsonLd item={item} />;
};
